/**
 * Multi-dashboard workspace: operational action queues + statistical recharts
 * views, tailored to each role and ready for future user-customizable layouts.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { TrendingUp } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api } from "@/api/client";
import { unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { downloadTableExport, TableExportButton } from "@/shared/components/table-export-button";
import {
  EmptyState,
  ErrorState,
  KpiCard,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "@/shared/components/page";
import { SitePicker } from "@/shared/components/pickers";
import { formatDate, formatPercent, formatQuantity } from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/ui/primitives";

import { MetricTrendDialog } from "./MetricTrendDialog";
import { MyWorkPage } from "./MyWorkPage";

export interface OperationalItem {
  id: string;
  code: string;
  title: string;
  severity?: string;
  priority?: string;
  status?: string;
  asset_name?: string;
  due_at?: string | null;
}

/** Shape of the dashboard payload (the endpoint returns a free-form object). */
export interface Dashboard {
  assets: {
    total: number;
    vessels: number;
    vessels_by_status: Record<string, number>;
    availability_pct: number | null;
  };
  work_orders: {
    open: number;
    overdue: number;
    due_this_week: number;
    awaiting_verification: number;
    by_priority: Record<string, number>;
    by_status: Record<string, number>;
    completed_last_30_days?: number;
    completed_in_period: number;
    completed_since: string;
    completed_until: string;
  };
  requests: { open: number; critical: number };
  pm: { due_this_week: number; overdue: number; compliance_pct: number | null };
  certificates: { expiring: number; expired: number };
  queues?: {
    untriaged_requests: number;
    unassigned_work_orders: number;
    awaiting_verification: number;
    warehouse_approvals: number;
    pending_part_issues: number;
    procurement_open: number;
    document_approvals: number;
    document_retention: number;
    chargebacks_pending: number;
    uncosted_labour: number;
    top_triage: OperationalItem[];
    top_verification: OperationalItem[];
    top_overdue: OperationalItem[];
  };
}

const PERIOD_OPTIONS = [7, 30, 90, 365] as const;

/** Fetch the dashboard for an optional site. */
export async function fetchDashboard(
  siteId?: string,
  days: 7 | 30 | 90 | 365 = 30,
): Promise<Dashboard> {
  return unwrap(
    await api.GET("/api/v1/reporting/dashboard", {
      params: { query: { site_id: siteId, days } },
    }),
  ) as unknown as Dashboard;
}

/** Horizontal breakdown list of `key → count` with status badges and optional drill-down. */
function Breakdown({
  title,
  data,
  namespace,
  to,
  filterKey,
}: {
  title: string;
  data: Record<string, number>;
  namespace: string;
  to?: string;
  filterKey?: string;
}): ReactElement {
  const entries = Object.entries(data).filter(([, n]) => n > 0);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">—</p>
        ) : (
          <ul className="space-y-1.5">
            {entries.map(([key, n]) => (
              <li key={key} className="flex items-center justify-between text-sm">
                {to && filterKey ? (
                  <Link
                    to={to}
                    search={{ [filterKey]: key } as never}
                    className="inline-flex items-center gap-1.5 hover:underline"
                  >
                    <StatusBadge value={key} namespace={namespace} />
                  </Link>
                ) : (
                  <StatusBadge value={key} namespace={namespace} />
                )}
                <span className="tabular-nums font-medium">{n}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Actionable operational queue card with direct links to resolve items. */
function OperationalQueueCard({
  title,
  count,
  viewAllTo,
  viewAllSearch,
  items,
  itemRoute,
  badgeNamespace,
}: {
  title: string;
  count: number;
  viewAllTo: string;
  viewAllSearch?: Record<string, unknown>;
  items: OperationalItem[];
  itemRoute: "/requests/$requestId" | "/work-orders/$woId";
  badgeNamespace: string;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <div className="flex items-center gap-2">
          <Badge tone={count > 0 ? "warning" : "outline"}>{count}</Badge>
          <Link
            to={viewAllTo}
            search={viewAllSearch as never}
            className="text-xs text-primary hover:underline"
          >
            {t("dashboard.viewQueue")} →
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("dashboard.queueClear")}</p>
        ) : (
          <ul className="divide-y text-sm">
            {items.map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <Link
                    to={itemRoute}
                    params={
                      (itemRoute === "/requests/$requestId"
                        ? { requestId: item.id }
                        : { woId: item.id }) as never
                    }
                    className="font-medium hover:underline"
                  >
                    <span className="font-mono text-xs text-muted-foreground">{item.code}</span>{" "}
                    {item.title}
                  </Link>
                  {item.asset_name ? (
                    <div className="truncate text-xs text-muted-foreground">
                      {item.asset_name}
                      {item.due_at ? ` · ${t("app.due")} ${formatDate(item.due_at)}` : ""}
                    </div>
                  ) : null}
                </div>
                {item.severity || item.priority ? (
                  <StatusBadge
                    value={String(item.severity ?? item.priority)}
                    namespace={badgeNamespace}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Landing route: role-aware multi-dashboard workspace for members allowed to see
 * reporting, otherwise the personal "My work" view.
 */
export function DashboardPage(): ReactElement {
  const { can } = useAuth();
  if (!can("reporting.dashboard.view")) {
    return <MyWorkPage />;
  }
  return <FleetDashboard />;
}

/** Role-aware default tab when none is in the URL. */
function defaultTabForRoles(
  roles: string[],
  canViewReports: boolean,
  canViewCosts: boolean,
  canViewStock: boolean,
): string {
  if (roles.includes("supervisor") || roles.includes("planner")) {
    return "operations";
  }
  if (roles.includes("asset_manager") && canViewReports) {
    return "compliance";
  }
  if ((roles.includes("storekeeper") || roles.includes("procurement")) && canViewStock) {
    return "supply";
  }
  if (roles.includes("finance") && canViewCosts) {
    return "finance";
  }
  if (roles.includes("admin") || canViewReports) {
    return "fleet";
  }
  return "my_work";
}

/** Multi-dashboard workspace. */
function FleetDashboard(): ReactElement {
  const { t } = useTranslation();
  const { me, can } = useAuth();
  const navigate = useNavigate();
  const search: {
    tab?: string;
    site_id?: string;
    days?: number;
  } = useSearch({ strict: false });
  const [trendMetric, setTrendMetric] = useState<{ key: string; label: string } | null>(null);

  const siteId = typeof search.site_id === "string" && search.site_id ? search.site_id : undefined;
  const days = PERIOD_OPTIONS.find((period) => period === search.days) ?? 30;
  const canViewReports = can("reporting.report.view");
  const canViewCosts = can("reporting.report.cost");
  const canViewStock =
    can("inventory.stock.view") || can("reporting.inventory.view") || canViewCosts;

  const activeTab =
    search.tab ?? defaultTabForRoles(me?.roles ?? [], canViewReports, canViewCosts, canViewStock);

  const setContext = (patch: { tab?: string; site_id?: string; days?: number }): void => {
    void navigate({
      to: "/",
      search: ((prev: Record<string, unknown>) => ({ ...prev, ...patch })) as never,
    });
  };

  const q = useQuery({
    queryKey: ["dashboard", siteId, days],
    queryFn: () => fetchDashboard(siteId, days),
    refetchInterval: 60_000,
  });

  const sinceIso = new Date(Date.now() - days * 86_400_000).toISOString();

  const reliabilityQuery = useQuery({
    queryKey: ["dashboard", "reliability", siteId, days],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reporting/reliability", {
          params: { query: { since: sinceIso, ...(siteId ? { site_id: siteId } : {}) } },
        }),
      ),
    enabled: canViewReports && (activeTab === "fleet" || activeTab === "operations"),
  });

  const utilisationQuery = useQuery({
    queryKey: ["dashboard", "utilisation", days],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reporting/utilisation", {
          params: { query: { since: sinceIso } },
        }),
      ),
    enabled: canViewReports && activeTab === "operations",
  });

  const pmComplianceQuery = useQuery({
    queryKey: ["dashboard", "pm-compliance", siteId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reporting/pm-compliance", {
          params: { query: siteId ? { site_id: siteId } : {} },
        }),
      ),
    enabled: canViewReports && (activeTab === "compliance" || activeTab === "operations"),
  });

  const certificatesQuery = useQuery({
    queryKey: ["dashboard", "certificates", siteId, days],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reporting/certificates", {
          params: { query: { days, ...(siteId ? { site_id: siteId } : {}) } },
        }),
      ),
    enabled: canViewReports && activeTab === "compliance",
  });

  const inventoryQuery = useQuery({
    queryKey: ["dashboard", "inventory", siteId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reporting/inventory", {
          params: { query: siteId ? { site_id: siteId } : {} },
        }),
      ),
    enabled: canViewStock && (activeTab === "supply" || activeTab === "finance"),
  });

  const costByMonthQuery = useQuery({
    queryKey: ["dashboard", "cost-by-month", siteId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reporting/cost/by-month", {
          params: { query: { months: 6, ...(siteId ? { site_id: siteId } : {}) } },
        }),
      ),
    enabled: canViewCosts && activeTab === "finance",
  });

  const costByAssetQuery = useQuery({
    queryKey: ["dashboard", "cost-by-asset", siteId, days],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reporting/cost/by-asset", {
          params: {
            query: { since: sinceIso, limit: 10, ...(siteId ? { site_id: siteId } : {}) },
          },
        }),
      ),
    enabled: canViewCosts && activeTab === "finance",
  });

  if (q.isPending) {
    return <LoadingState />;
  }
  if (q.isError) {
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  }
  const d = q.data;
  const queues = d.queues;
  const completedKpi = (
    <KpiCard
      label={t("feedbackDashboard.completed", { days })}
      value={d.work_orders.completed_in_period}
      hint={t("feedbackDashboard.completedHint")}
      to="/work-orders"
      search={{
        status: "completed,verified,closed",
        open_only: false,
        site_id: siteId,
        completed_after: d.work_orders.completed_since,
        completed_before: d.work_orders.completed_until,
      }}
    />
  );

  const priorityChartData = Object.entries(d.work_orders.by_priority).map(([key, count]) => ({
    name: t(`workOrders.priority.${key}`, { defaultValue: key }),
    count,
  }));

  const costMonthData = (costByMonthQuery.data ?? []).map((row) => ({
    month: typeof row.month === "string" ? row.month : "",
    labour: Number(row.labour ?? 0),
    parts: Number(row.parts ?? 0),
    vendor: Number(row.vendor ?? 0),
  }));

  const certRows = (certificatesQuery.data ?? []) as {
    certificate_id: string;
    asset_id?: string;
    asset_code: string;
    asset_name: string;
    name: string;
    expires_on: string;
    days_to_expiry: number | null;
    status: string;
  }[];

  const reliabilityRows = (reliabilityQuery.data ?? []) as {
    asset_id: string;
    asset_code: string;
    asset_name: string;
    failures: number;
    mttr_hours: number | null;
    mtbf_hours: number | null;
    downtime_hours: number;
    availability_pct: number | null;
  }[];

  const utilisationRows = (utilisationQuery.data ?? []) as {
    membership_id: string;
    name: string;
    hours: string | number;
    work_orders: number;
    utilisation_pct: number;
  }[];

  const pmPlans = ((pmComplianceQuery.data as { plans?: unknown[] } | undefined)?.plans ?? []) as {
    plan_code: string;
    plan_name: string;
    total: number;
    compliant: number;
    overdue: number;
    compliance_pct: number | null;
  }[];

  const invData = inventoryQuery.data as
    | {
        total_value?: string | number;
        by_location?: { location_code: string; items: number; value: string | number }[];
        below_minimum?: {
          sku: string;
          name: string;
          location_code: string;
          on_hand: string | number;
          min_qty: string | number;
          suggested_qty: string | number;
        }[];
      }
    | undefined;

  const costAssetRows = (costByAssetQuery.data ?? []) as {
    asset_id: string;
    asset_code: string;
    asset_name: string;
    work_orders: number;
    labour: string | number;
    parts: string | number;
    vendor: string | number;
    total: string | number;
  }[];

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("dashboard.title")}
        subtitle={me?.active_tenant.name}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-52">
              <SitePicker
                id="dashboard-site"
                value={siteId ?? ""}
                placeholder={t("reports.allSites")}
                onChange={(id) => setContext({ site_id: id ?? undefined })}
              />
            </div>
            <div
              className="inline-flex rounded-md border bg-muted/30 p-0.5"
              role="group"
              aria-label={t("reports.days")}
            >
              {PERIOD_OPTIONS.map((p) => (
                <Button
                  key={p}
                  size="sm"
                  variant={days === p ? "secondary" : "ghost"}
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setContext({ days: p })}
                >
                  {p === 365 ? "12m" : `${String(p)}d`}
                </Button>
              ))}
            </div>
          </div>
        }
      />

      {trendMetric ? (
        <MetricTrendDialog
          metricKey={trendMetric.key}
          label={trendMetric.label}
          siteId={siteId}
          days={days}
          onClose={() => setTrendMetric(null)}
        />
      ) : null}

      <Tabs value={activeTab} onValueChange={(tab) => setContext({ tab })}>
        <TabsList className="mb-4 flex w-full flex-wrap justify-start">
          <TabsTrigger value="fleet">{t("dashboard.tabs.fleet")}</TabsTrigger>
          <TabsTrigger value="operations">{t("dashboard.tabs.operations")}</TabsTrigger>
          <TabsTrigger value="compliance">{t("dashboard.tabs.compliance")}</TabsTrigger>
          {canViewStock ? (
            <TabsTrigger value="supply">{t("dashboard.tabs.supply")}</TabsTrigger>
          ) : null}
          {canViewCosts ? (
            <TabsTrigger value="finance">{t("dashboard.tabs.finance")}</TabsTrigger>
          ) : null}
          <TabsTrigger value="my_work">{t("dashboard.tabs.myWork")}</TabsTrigger>
        </TabsList>

        {/* TAB 1: FLEET & EXECUTIVE OVERVIEW */}
        <TabsContent value="fleet" className="space-y-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            {completedKpi}
            <KpiCard
              label={t("dashboard.fleetAvailability")}
              value={formatPercent(d.assets.availability_pct)}
              hint={t("assets.ownedVessels", { count: d.assets.vessels })}
              tone={
                d.assets.availability_pct !== null && d.assets.availability_pct < 80
                  ? "warning"
                  : "success"
              }
              to="/map"
              search={{ ownership: "owned" }}
            />
            <KpiCard
              label={t("dashboard.openWorkOrders")}
              value={d.work_orders.open}
              to="/work-orders"
              search={{ status: "draft,planned,scheduled,in_progress,on_hold", site_id: siteId }}
            />
            <KpiCard
              label={t("dashboard.overdueWorkOrders")}
              value={d.work_orders.overdue}
              tone={d.work_orders.overdue > 0 ? "danger" : "neutral"}
              to="/work-orders"
              search={{ overdue: true, site_id: siteId }}
            />
            <KpiCard
              label={t("dashboard.dueThisWeek")}
              value={d.work_orders.due_this_week}
              to="/work-orders"
              search={{
                status: "draft,planned,scheduled,in_progress,on_hold",
                due_after: new Date().toISOString(),
                due_before: new Date(Date.now() + 7 * 86400000).toISOString(),
                site_id: siteId,
              }}
            />
            <KpiCard
              label={t("dashboard.awaitingVerification")}
              value={d.work_orders.awaiting_verification}
              tone={d.work_orders.awaiting_verification > 0 ? "info" : "neutral"}
              to="/work-orders"
              search={{ status: "completed", site_id: siteId }}
            />
            <KpiCard
              label={t("dashboard.openRequests")}
              value={d.requests.open}
              hint={`${d.requests.critical} ${t("dashboard.criticalRequests").toLowerCase()}`}
              tone={d.requests.critical > 0 ? "danger" : "neutral"}
              to="/requests"
              search={{ status: "new,acknowledged,in_review,converted", site_id: siteId }}
            />
            <KpiCard
              label={t("dashboard.pmCompliance")}
              value={formatPercent(d.pm.compliance_pct)}
              tone={
                d.pm.compliance_pct !== null && d.pm.compliance_pct < 90 ? "warning" : "success"
              }
              to="/maintenance"
              search={{ site_id: siteId }}
            />
            <KpiCard
              label={t("dashboard.pmDueThisWeek")}
              value={d.pm.due_this_week}
              to="/maintenance"
              search={{
                status: "scheduled",
                due_before: new Date(Date.now() + 7 * 86400000).toISOString(),
                site_id: siteId,
              }}
            />
            <KpiCard
              label={t("dashboard.pmOverdue")}
              value={d.pm.overdue}
              tone={d.pm.overdue > 0 ? "danger" : "neutral"}
              to="/maintenance"
              search={{ status: "overdue", site_id: siteId }}
            />
            <KpiCard
              label={t("dashboard.certsExpiring")}
              value={d.certificates.expiring}
              tone={d.certificates.expiring > 0 ? "warning" : "neutral"}
              to="/"
              search={{ tab: "compliance", site_id: siteId }}
            />
            <KpiCard
              label={t("dashboard.certsExpired")}
              value={d.certificates.expired}
              tone={d.certificates.expired > 0 ? "danger" : "neutral"}
              to="/"
              search={{ tab: "compliance", site_id: siteId }}
            />
            <KpiCard
              label={t("nav.assets")}
              value={d.assets.total}
              to="/assets"
              search={{ site_id: siteId }}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {t("dashboard.inspectTrend")}:
            </span>
            {[
              { key: "assets.availability_pct", label: t("dashboard.fleetAvailability") },
              { key: "workorders.overdue", label: t("dashboard.overdueWorkOrders") },
              { key: "workorders.open", label: t("dashboard.openWorkOrders") },
              { key: "pm.compliance_pct", label: t("dashboard.pmCompliance") },
            ].map((m) => (
              <Button
                key={m.key}
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-xs"
                onClick={() => setTrendMetric(m)}
              >
                <TrendingUp className="size-3.5" aria-hidden />
                {m.label}
              </Button>
            ))}
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <Breakdown
              title={t("dashboard.vesselsByStatus")}
              data={d.assets.vessels_by_status}
              namespace="assets.status"
              to="/assets"
              filterKey="status"
            />
            <Breakdown
              title={t("dashboard.byStatus")}
              data={d.work_orders.by_status}
              namespace="workOrders.status"
              to="/work-orders"
              filterKey="status"
            />
            <Breakdown
              title={t("dashboard.byPriority")}
              data={d.work_orders.by_priority}
              namespace="workOrders.priority"
              to="/work-orders"
              filterKey="priority"
            />
          </div>

          {canViewReports && reliabilityRows.length > 0 ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2">
                <CardTitle>{t("dashboard.reliabilityTitle")}</CardTitle>
                <TableExportButton
                  onExport={(format) =>
                    downloadTableExport(
                      "/api/v1/reporting/reliability",
                      "reliability",
                      { since: sinceIso, site_id: siteId },
                      format,
                    )
                  }
                />
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-start text-xs text-muted-foreground">
                      <th className="py-2 text-start">{t("app.asset")}</th>
                      <th className="py-2 text-end">{t("dashboard.failures")}</th>
                      <th className="py-2 text-end">MTTR (h)</th>
                      <th className="py-2 text-end">MTBF (h)</th>
                      <th className="py-2 text-end">{t("dashboard.downtimeHours")}</th>
                      <th className="py-2 text-end">{t("dashboard.fleetAvailability")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {reliabilityRows.slice(0, 10).map((row) => (
                      <tr key={row.asset_id}>
                        <td className="py-2">
                          <Link
                            to="/assets/$assetId"
                            params={{ assetId: row.asset_id }}
                            className="font-medium hover:underline"
                          >
                            <span className="font-mono text-xs text-muted-foreground">
                              {row.asset_code}
                            </span>{" "}
                            {row.asset_name}
                          </Link>
                        </td>
                        <td className="py-2 text-end tabular-nums">{row.failures}</td>
                        <td className="py-2 text-end tabular-nums">{row.mttr_hours ?? "—"}</td>
                        <td className="py-2 text-end tabular-nums">{row.mtbf_hours ?? "—"}</td>
                        <td className="py-2 text-end tabular-nums">{row.downtime_hours}</td>
                        <td className="py-2 text-end tabular-nums">
                          {formatPercent(row.availability_pct)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>

        {/* TAB 2: MAINTENANCE CONTROL (SUPERVISOR / PLANNER) */}
        <TabsContent value="operations" className="space-y-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {completedKpi}
            <KpiCard
              label={t("dashboard.untriagedFaults")}
              value={queues?.untriaged_requests ?? 0}
              tone={(queues?.untriaged_requests ?? 0) > 0 ? "warning" : "neutral"}
              to="/requests"
              search={{ status: "new,acknowledged", site_id: siteId }}
            />
            <KpiCard
              label={t("dashboard.awaitingVerification")}
              value={d.work_orders.awaiting_verification}
              tone={d.work_orders.awaiting_verification > 0 ? "info" : "neutral"}
              to="/work-orders"
              search={{ status: "completed", site_id: siteId }}
            />
            <KpiCard
              label={t("dashboard.unassignedWorkOrders")}
              value={queues?.unassigned_work_orders ?? 0}
              tone={(queues?.unassigned_work_orders ?? 0) > 0 ? "warning" : "neutral"}
              to="/work-orders"
              search={{ open_only: true, site_id: siteId }}
            />
            <KpiCard
              label={t("dashboard.overdueWorkOrders")}
              value={d.work_orders.overdue}
              tone={d.work_orders.overdue > 0 ? "danger" : "neutral"}
              to="/work-orders"
              search={{ overdue: true, site_id: siteId }}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <OperationalQueueCard
              title={t("dashboard.untriagedFaults")}
              count={queues?.untriaged_requests ?? 0}
              viewAllTo="/requests"
              viewAllSearch={{ status: "new,acknowledged", site_id: siteId }}
              items={queues?.top_triage ?? []}
              itemRoute="/requests/$requestId"
              badgeNamespace="requests.severity"
            />
            <OperationalQueueCard
              title={t("dashboard.awaitingVerification")}
              count={d.work_orders.awaiting_verification}
              viewAllTo="/work-orders"
              viewAllSearch={{ status: "completed", site_id: siteId }}
              items={queues?.top_verification ?? []}
              itemRoute="/work-orders/$woId"
              badgeNamespace="workOrders.priority"
            />
            <OperationalQueueCard
              title={t("dashboard.overdueWorkOrders")}
              count={d.work_orders.overdue}
              viewAllTo="/work-orders"
              viewAllSearch={{ overdue: true, site_id: siteId }}
              items={queues?.top_overdue ?? []}
              itemRoute="/work-orders/$woId"
              badgeNamespace="workOrders.priority"
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {priorityChartData.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle>{t("dashboard.byPriority")}</CardTitle>
                </CardHeader>
                <CardContent className="h-60">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={priorityChartData}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            ) : null}

            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2">
                <CardTitle>{t("dashboard.technicianUtilisation")}</CardTitle>
                {canViewReports ? (
                  <TableExportButton
                    onExport={(format) =>
                      downloadTableExport(
                        "/api/v1/reporting/utilisation",
                        "technician-utilisation",
                        { since: sinceIso },
                        format,
                      )
                    }
                  />
                ) : null}
              </CardHeader>
              <CardContent>
                {utilisationRows.length === 0 ? (
                  <EmptyState message={t("dashboard.noUtilisation")} />
                ) : (
                  <ul className="divide-y text-sm">
                    {utilisationRows.slice(0, 8).map((u) => (
                      <li key={u.membership_id} className="flex items-center justify-between py-2">
                        <span className="font-medium">{u.name}</span>
                        <span className="tabular-nums text-muted-foreground">
                          {formatQuantity(u.hours)} h · {u.work_orders} WO ·{" "}
                          <strong>{formatPercent(u.utilisation_pct)}</strong>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB 3: ASSETS, COMPLIANCE & DOCUMENTS */}
        <TabsContent value="compliance" className="space-y-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KpiCard
              label={t("dashboard.certsExpired")}
              value={d.certificates.expired}
              tone={d.certificates.expired > 0 ? "danger" : "neutral"}
              to="/assets"
              search={{ vessels_only: true, site_id: siteId }}
            />
            <KpiCard
              label={t("dashboard.certsExpiring")}
              value={d.certificates.expiring}
              tone={d.certificates.expiring > 0 ? "warning" : "neutral"}
              to="/assets"
              search={{ vessels_only: true, site_id: siteId }}
            />
            <KpiCard
              label={t("dashboard.docApprovals")}
              value={queues?.document_approvals ?? 0}
              tone={(queues?.document_approvals ?? 0) > 0 ? "info" : "neutral"}
              to="/documents"
              search={{ tab: "publications" }}
            />
            <KpiCard
              label={t("dashboard.docRetention")}
              value={queues?.document_retention ?? 0}
              tone={(queues?.document_retention ?? 0) > 0 ? "warning" : "neutral"}
              to="/documents"
              search={{ tab: "retention" }}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between gap-2">
                <CardTitle>{t("dashboard.upcomingCertificates")}</CardTitle>
                <TableExportButton
                  onExport={(format) =>
                    downloadTableExport(
                      "/api/v1/reporting/certificates",
                      "certificates",
                      { days, site_id: siteId },
                      format,
                    )
                  }
                />
              </CardHeader>
              <CardContent>
                {certRows.length === 0 ? (
                  <EmptyState message={t("dashboard.noExpiringCerts")} />
                ) : (
                  <ul className="divide-y text-sm">
                    {certRows.slice(0, 10).map((c) => (
                      <li
                        key={c.certificate_id}
                        className="flex items-center justify-between gap-2 py-2"
                      >
                        <div className="min-w-0">
                          <div className="font-medium">
                            {c.asset_id ? (
                              <Link
                                to="/assets/$assetId"
                                params={{ assetId: c.asset_id }}
                                className="hover:underline"
                              >
                                <span className="font-mono text-xs text-muted-foreground">
                                  {c.asset_code}
                                </span>{" "}
                                {c.name}
                              </Link>
                            ) : (
                              <>
                                <span className="font-mono text-xs text-muted-foreground">
                                  {c.asset_code}
                                </span>{" "}
                                {c.name}
                              </>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {c.asset_name} · {c.expires_on}
                          </div>
                        </div>
                        <Badge
                          tone={
                            (c.days_to_expiry ?? 0) < 0
                              ? "danger"
                              : (c.days_to_expiry ?? 99) <= 30
                                ? "warning"
                                : "outline"
                          }
                        >
                          {c.days_to_expiry !== null ? `${String(c.days_to_expiry)}d` : c.status}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t("dashboard.pmCompliance")}</CardTitle>
              </CardHeader>
              <CardContent>
                {pmPlans.length === 0 ? (
                  <EmptyState message={t("dashboard.noPmPlans")} />
                ) : (
                  <ul className="divide-y text-sm">
                    {pmPlans.map((p) => (
                      <li
                        key={p.plan_code}
                        className="flex items-center justify-between gap-2 py-2"
                      >
                        <div className="min-w-0">
                          <span className="font-mono text-xs text-muted-foreground">
                            {p.plan_code}
                          </span>{" "}
                          <span className="font-medium">{p.plan_name}</span>
                        </div>
                        <div className="flex items-center gap-2 tabular-nums">
                          {p.overdue > 0 ? (
                            <Badge tone="danger">
                              {p.overdue} {t("workOrders.overdueOnly").toLowerCase()}
                            </Badge>
                          ) : null}
                          <span>{formatPercent(p.compliance_pct)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB 4: SUPPLY CHAIN & WAREHOUSE */}
        {canViewStock ? (
          <TabsContent value="supply" className="space-y-6">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <KpiCard
                label={t("dashboard.warehouseApprovals")}
                value={queues?.warehouse_approvals ?? 0}
                tone={(queues?.warehouse_approvals ?? 0) > 0 ? "warning" : "neutral"}
                to="/warehouse"
                search={{ tab: "records", needs_review: true }}
              />
              <KpiCard
                label={t("dashboard.belowMinItems")}
                value={invData?.below_minimum?.length ?? 0}
                tone={(invData?.below_minimum?.length ?? 0) > 0 ? "danger" : "neutral"}
                to="/inventory"
                search={{ below_min: true }}
              />
              <KpiCard
                label={t("dashboard.pendingPartIssues")}
                value={queues?.pending_part_issues ?? 0}
                to="/work-orders"
                search={{ open_only: true }}
              />
              <KpiCard
                label={t("dashboard.openProcurement")}
                value={queues?.procurement_open ?? 0}
                to="/inventory"
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t("dashboard.belowMinItems")}</CardTitle>
                </CardHeader>
                <CardContent>
                  {!invData?.below_minimum?.length ? (
                    <EmptyState message={t("dashboard.stockHealthy")} />
                  ) : (
                    <ul className="divide-y text-sm">
                      {invData.below_minimum.slice(0, 10).map((item, idx) => (
                        <li key={idx} className="flex items-center justify-between gap-2 py-2">
                          <div>
                            <span className="font-mono text-xs text-muted-foreground">
                              {item.sku}
                            </span>{" "}
                            <span className="font-medium">{item.name}</span>
                            <div className="text-xs text-muted-foreground">
                              {item.location_code}
                            </div>
                          </div>
                          <div className="text-end text-xs tabular-nums">
                            <div>
                              {formatQuantity(item.on_hand)} / {formatQuantity(item.min_qty)}
                            </div>
                            <div className="font-medium text-primary">
                              +{formatQuantity(item.suggested_qty)}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t("dashboard.stockByLocation")}</CardTitle>
                </CardHeader>
                <CardContent>
                  {!invData?.by_location?.length ? (
                    <EmptyState message={t("dashboard.noStockLocations")} />
                  ) : (
                    <ul className="divide-y text-sm">
                      {invData.by_location.map((loc) => (
                        <li
                          key={loc.location_code}
                          className="flex items-center justify-between py-2"
                        >
                          <span className="font-medium">{loc.location_code}</span>
                          <span className="tabular-nums text-muted-foreground">
                            {loc.items} · <strong>{formatQuantity(loc.value)}</strong>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        ) : null}

        {/* TAB 5: MAINTENANCE FINANCE */}
        {canViewCosts ? (
          <TabsContent value="finance" className="space-y-6">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <KpiCard
                label={t("dashboard.chargebacksPending")}
                value={queues?.chargebacks_pending ?? 0}
                tone={(queues?.chargebacks_pending ?? 0) > 0 ? "warning" : "neutral"}
                to="/finance"
                search={{ tab: "chargebacks", chargeback_status: "draft" }}
              />
              <KpiCard
                label={t("dashboard.uncostedLabour")}
                value={queues?.uncosted_labour ?? 0}
                tone={(queues?.uncosted_labour ?? 0) > 0 ? "danger" : "neutral"}
                to="/finance"
              />
              <KpiCard
                label={t("dashboard.totalInventoryValue")}
                value={formatQuantity(invData?.total_value ?? 0)}
                to="/warehouse"
              />
              {completedKpi}
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2">
                  <CardTitle>{t("dashboard.monthlyCostBreakdown")}</CardTitle>
                  <TableExportButton
                    onExport={(format) =>
                      downloadTableExport(
                        "/api/v1/reporting/cost/by-month",
                        "cost-by-month",
                        { months: 6, site_id: siteId },
                        format,
                      )
                    }
                  />
                </CardHeader>
                <CardContent className="h-64">
                  {costMonthData.length === 0 ? (
                    <EmptyState message={t("dashboard.noCostData")} />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={costMonthData}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        <Bar
                          dataKey="labour"
                          stackId="a"
                          fill="#2563eb"
                          name={t("dashboard.labour")}
                        />
                        <Bar
                          dataKey="parts"
                          stackId="a"
                          fill="#0d9488"
                          name={t("dashboard.parts")}
                        />
                        <Bar
                          dataKey="vendor"
                          stackId="a"
                          fill="#f59e0b"
                          name={t("dashboard.vendor")}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2">
                  <CardTitle>{t("dashboard.topCostAssets")}</CardTitle>
                  <TableExportButton
                    onExport={(format) =>
                      downloadTableExport(
                        "/api/v1/reporting/cost/by-asset",
                        "cost-by-asset",
                        { since: sinceIso, site_id: siteId },
                        format,
                      )
                    }
                  />
                </CardHeader>
                <CardContent>
                  {costAssetRows.length === 0 ? (
                    <EmptyState message={t("dashboard.noCostData")} />
                  ) : (
                    <ul className="divide-y text-sm">
                      {costAssetRows.map((row) => (
                        <li
                          key={row.asset_id}
                          className="flex items-center justify-between gap-2 py-2"
                        >
                          <Link
                            to="/assets/$assetId"
                            params={{ assetId: row.asset_id }}
                            className="font-medium hover:underline"
                          >
                            <span className="font-mono text-xs text-muted-foreground">
                              {row.asset_code}
                            </span>{" "}
                            {row.asset_name}
                          </Link>
                          <span className="tabular-nums font-medium">
                            {formatQuantity(row.total)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        ) : null}

        {/* TAB 6: MY WORKSPACE */}
        <TabsContent value="my_work">
          <MyWorkPage />
        </TabsContent>
      </Tabs>
    </div>
  );
}
